import React, {useEffect, useState} from "react";
import {
    ActiveBadge,
    Container,
    Grid,
    MyExtensionsGroup,
    MyExtensionsLabel,
    PageHeader,
    PageSubtitle,
    PageTitle,
    PageTitleGroup,
    SubscriptionWarningBanner,
    SubscriptionWarningText,
} from "./styled";
import {useInjection} from "inversify-react";
import {observer} from "mobx-react";
import {AppController} from "@dropDesk/presentation/app/app.controller";
import {ExtensionsController} from "@dropDesk/presentation/pages/extensions/controller/extensions.controller";
import DropDeskLoading from "@dropDesk/presentation/components/loadings/loading_dropdesk";
import {PlanExtensionCatalogEntity} from "@dropDesk/domain/entities/plan_extensions/plan_extension_catalog.entity";
import ExtensionCard from "./extension_card";
import ExtensionHighlightCard from "./extension_highlight_card";
import {useNavigate} from "react-router-dom";
import {RoutesEnum} from "@dropDesk/domain/entities/routes/routes_enum";
import ModalsExtensions from "@dropDesk/presentation/pages/extensions/ui/modals";
import {useBetween} from "use-between";
import {useModalExtensions} from "@dropDesk/presentation/pages/extensions/ui/modals/handle_change_visible";
import DropDeskIcon from "@dropDesk/storage/icons";
import {ICONS_DROPDESK} from "@dropDesk/storage/icons/icons.d";

const StoreExtension = (observer(() => {
    const controller = useInjection(ExtensionsController);
    const appController = useInjection(AppController);
    const colors = appController.theme.colorScheme;
    const navigate = useNavigate();

    const [showOnlyActive, setShowOnlyActive] = useState(false);
    const subscriptionCanceledScheduled = appController.user?.company?.newSubs?.isCanceledScheduled(appController.user) ?? false;
    const isTrialAccount = appController.user?.company?.newSubs?.isFreeActiveSubscription ?? false;
    const {
        setVisibleModalCancelExtension,
        setVisibleModalReactivateExtension,
        setVisibleModalCancelActivation,
    } = useBetween(useModalExtensions);

    useEffect(() => {
        controller.initialize();
    }, []);

    const activeCount = controller.extensions.filter(e => !!e.installed).length;
    const visibleExtensions = showOnlyActive
        ? controller.extensions.filter(e => !!e.installed)
        : controller.extensions;

    const handleAction = (extension: PlanExtensionCatalogEntity) => {
        navigate(`${RoutesEnum.subscription}?idExtension=${extension.id}`);
    };

    const handleCancel = (extension: PlanExtensionCatalogEntity) => {
        setVisibleModalCancelExtension({visible: true, extension});
    };

    const handleReactivate = (extension: PlanExtensionCatalogEntity) => {
        setVisibleModalReactivateExtension({visible: true, extension});
    };

    const handlePay = (extension: PlanExtensionCatalogEntity) => {
        navigate(`${RoutesEnum.subscription}?idPaymentExtension=${extension.id}`);
    };

    const handleCancelActivation = (extension: PlanExtensionCatalogEntity) => {
        setVisibleModalCancelActivation({visible: true, extension});
    };

    const handleToggleActive = () => {
        if (activeCount > 0) setShowOnlyActive(prev => !prev);
    };

    return (
        <Container background={colors.onBackground}>
            {controller.loading && (
                <DropDeskLoading height={250} description={controller.loadingMessage}/>
            )}

            {!controller.loading && (
                <>
                    <PageHeader>
                        <PageTitleGroup>
                            <PageTitle color={colors.text}>
                                {showOnlyActive ? 'Minhas Extensões' : 'Loja de Extensões'}
                            </PageTitle>
                            <PageSubtitle color={colors.hint}>
                                {showOnlyActive
                                    ? 'Extensões ativas na sua conta.'
                                    : 'Expanda seu atendimento com recursos adicionais para sua operação.'
                                }
                            </PageSubtitle>
                        </PageTitleGroup>

                        <MyExtensionsGroup>
                            <MyExtensionsLabel color={colors.text}>Minhas extensões</MyExtensionsLabel>
                            <ActiveBadge
                                background={showOnlyActive ? `${colors.accent}30` : `${colors.accent}15`}
                                color={colors.accent}
                                clickable={activeCount > 0}
                                onClick={handleToggleActive}
                            >
                                {showOnlyActive
                                    ? 'Ver todas'
                                    : `${activeCount} ${activeCount === 1 ? 'extensão ativa' : 'extensões ativas'}`
                                }
                            </ActiveBadge>
                        </MyExtensionsGroup>
                    </PageHeader>

                    {subscriptionCanceledScheduled && (
                        <SubscriptionWarningBanner
                            background={`${colors.warning}15`}
                            border={`${colors.warning}40`}
                        >
                            <DropDeskIcon icon={ICONS_DROPDESK.warning} size={16} color={colors.warning}/>
                            <SubscriptionWarningText color={colors.text}>
                                Sua assinatura está com cancelamento agendado. As ações nas extensões estão desabilitadas até que a assinatura seja reativada.
                            </SubscriptionWarningText>
                        </SubscriptionWarningBanner>
                    )}

                    <Grid>
                        {visibleExtensions.map((extension) => (
                            <ExtensionCard
                                key={extension.id}
                                extension={extension}
                                onAction={handleAction}
                                onCancel={handleCancel}
                                onReactivate={handleReactivate}
                                onPay={handlePay}
                                onCancelActivation={handleCancelActivation}
                                subscriptionCanceledScheduled={subscriptionCanceledScheduled}
                                isTrialAccount={isTrialAccount}
                                onSubscribe={() => navigate(RoutesEnum.subscription)}
                            />
                        ))}
                    </Grid>

                    {!showOnlyActive && <ExtensionHighlightCard extensions={controller.extensions}/>}
                </>
            )}
            <ModalsExtensions
                loading={controller.loading}
                onConfirmCancel={(extensionId) => controller.unsubscribe(extensionId)}
                onConfirmReactivate={(extensionId) => controller.reactivate(extensionId)}
            />
        </Container>
    );
}));

export default StoreExtension;
